import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PromoBannerCarousel.css';

export const PROMO_BANNERS = [
  '/banners/0716c289914e.webp',
  '/banners/22a93acd65fe.webp',
  '/banners/2c3d1c14b9b9.webp',
  '/banners/462ca12fea89.webp',
  '/banners/ae2d162cb7f5.webp',
  '/banners/ec95c4074cef.webp',
];

const SLIDE_GAP = 8;
const AUTO_SCROLL_MS = 5000;

const PromoBannerCarousel: React.FC = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [visibleCount, setVisibleCount] = useState(2);
  const [slideWidth, setSlideWidth] = useState(300);
  const [isPaused, setIsPaused] = useState(false);

  const updateLayout = useCallback(() => {
    const isPhone = window.innerWidth <= 768;
    const nextVisible = isPhone ? 1 : 2;
    setVisibleCount(nextVisible);

    const viewport = viewportRef.current;
    if (!viewport) return;

    const width = viewport.clientWidth;
    if (isPhone) {
      // Lotus mobile: 310px slide + 8px gap + ~34px peek of next
      const peek = 34;
      const lotusSlide = 310;
      const nextSlideWidth = Math.min(lotusSlide, Math.max(width - peek, 200));
      setSlideWidth(nextSlideWidth);
      return;
    }

    const nextSlideWidth = Math.floor((width - SLIDE_GAP * (nextVisible - 1)) / nextVisible);
    setSlideWidth(Math.max(nextSlideWidth, 200));
  }, []);

  useEffect(() => {
    updateLayout();
    window.addEventListener('resize', updateLayout);

    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', updateLayout);
    }

    const observer = new ResizeObserver(updateLayout);
    observer.observe(viewport);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateLayout);
    };
  }, [updateLayout]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setIsLoading(false), 4000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const handleImageLoad = useCallback(() => {
    setLoadedCount((count) => {
      const next = count + 1;
      if (next >= Math.min(PROMO_BANNERS.length, visibleCount)) {
        setIsLoading(false);
      }
      return next;
    });
  }, [visibleCount]);

  const maxIndex = Math.max(0, PROMO_BANNERS.length - visibleCount);

  useEffect(() => {
    setCurrentIndex((index) => Math.min(index, maxIndex));
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((index) => (index <= 0 ? maxIndex : index - 1));
  }, [maxIndex]);

  const goNext = useCallback(() => {
    setCurrentIndex((index) => (index >= maxIndex ? 0 : index + 1));
  }, [maxIndex]);

  useEffect(() => {
    if (isPaused || isLoading || maxIndex === 0) return undefined;

    const intervalId = window.setInterval(goNext, AUTO_SCROLL_MS);
    return () => window.clearInterval(intervalId);
  }, [goNext, isLoading, isPaused, maxIndex]);

  const showSkeleton = isLoading && loadedCount < Math.min(PROMO_BANNERS.length, visibleCount);
  const step = slideWidth + SLIDE_GAP;

  return (
    <section
      className="promo-banner-section lotus-promo-carousel"
      aria-label="Promotional banners"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{ ['--promo-slide-width' as string]: `${slideWidth}px`, ['--promo-slide-gap' as string]: `${SLIDE_GAP}px` }}
    >
      {showSkeleton && (
        <div className="promo-carousel-skeleton" aria-hidden="true">
          {Array.from({ length: visibleCount }).map((_, index) => (
            <div key={index} className="lotus-skeleton lotus-skeleton-promo-slide" />
          ))}
        </div>
      )}

      <button
        type="button"
        className="promo-carousel-nav promo-carousel-nav--prev"
        onClick={goPrev}
        aria-label="Previous banners"
      >
        <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M7.5 9.5L4 6L7.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div
        ref={viewportRef}
        className={`promo-carousel-viewport ${showSkeleton ? 'promo-carousel-viewport--loading' : ''}`}
      >
        <div
          className="promo-carousel-track lotus-promo-track"
          style={{ transform: `translateX(-${currentIndex * step}px)` }}
        >
          {PROMO_BANNERS.map((src, index) => (
            <div key={src} className="promo-slide">
              <img
                src={src}
                alt={`Promotion banner ${index + 1}`}
                loading={index < visibleCount ? 'eager' : 'lazy'}
                onLoad={handleImageLoad}
              />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="promo-carousel-nav promo-carousel-nav--next"
        onClick={goNext}
        aria-label="Next banners"
      >
        <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </section>
  );
};

export default PromoBannerCarousel;
